import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../lib/api";
import { motion, AnimatePresence } from "framer-motion";

const OPTIONS = [
  { key: "love_well", label: "Loved", swatch: "#4A7C7B" },
  { key: "thankful",  label: "Thankful", swatch: "#D4A373" },
  { key: "listening_inside", label: "Listening Inside", swatch: "#D28A7C" },
  { key: "small_strong", label: "Small and Strong", swatch: "#2F5453" },
];

export default function FeelingTool() {
  const [active, setActive] = useState("love_well");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/feelings/${active}`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [active]);

  return (
    <section data-testid="feeling-tool" className="relative py-24 md:py-32 overflow-hidden">
      <div className="max-w-[1200px] mx-auto px-6 md:px-10">
        <div className="grid md:grid-cols-12 gap-6 items-end mb-14">
          <div className="md:col-span-8">
            <div className="mono text-[11px] tracking-[0.28em] uppercase text-[#4A7C7B] mb-4">A starting place for hard conversations</div>
            <h2 className="serif text-[clamp(2.25rem,5.5vw,4.5rem)] leading-[1.02] tracking-tight text-espresso">
              How is your child <span className="italic text-[#4A7C7B]">feeling</span> today?
            </h2>
          </div>
          <div className="md:col-span-4 text-sm text-charcoal/85 leading-relaxed">
            Pick one. Charlie offers a short reflection in the child's language, and a small, useable next step for the grown-up.
          </div>
        </div>

        <div className="flex flex-wrap gap-3 mb-10">
          {OPTIONS.map((o) => {
            const isActive = active === o.key;
            return (
              <button
                key={o.key}
                data-testid={`feeling-pill-${o.key}`}
                onClick={() => setActive(o.key)}
                className={`feeling-pill inline-flex items-center gap-3 rounded-full pl-3 pr-5 py-2.5 border ${
                  isActive
                    ? "bg-[#2C241B] text-[#FAF8F5] border-[#2C241B]"
                    : "bg-[#FAF8F5] text-espresso border-black/10 hover:border-[#4A7C7B]/60"
                }`}
              >
                <span className="w-3 h-3 rounded-full" style={{ background: o.swatch }} />
                <span className="text-sm tracking-wide">{o.label}</span>
              </button>
            );
          })}
        </div>

        <div className="grid md:grid-cols-12 gap-8">
          <div className="md:col-span-7">
            <div className="relative torn-frame bg-[#FFFDF9] p-8 md:p-12 min-h-[340px]">
              <AnimatePresence mode="wait">
                {data && !loading && (
                  <motion.div
                    key={data.feeling}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -12 }}
                    transition={{ duration: 0.45, ease: [0.65, 0, 0.35, 1] }}
                  >
                    <div className="mono text-[10px] tracking-[0.28em] uppercase text-[#4A7C7B]">
                      Chapter · {data.title}
                    </div>
                    <blockquote data-testid="feeling-quote" className="italic-serif text-2xl md:text-3xl leading-[1.25] text-espresso mt-6 border-l-2 border-[#4A7C7B] pl-5">
                      "{data.charlie_quote}"
                      <span className="mono text-[10px] tracking-[0.28em] uppercase text-charcoal/70 not-italic mt-4 block">— Charlie</span>
                    </blockquote>
                    <div className="mt-8">
                      <div className="mono text-[10px] tracking-[0.28em] uppercase text-[#D28A7C]">For the grown-up</div>
                      <p data-testid="feeling-tip" className="mt-2 text-base md:text-lg leading-relaxed text-charcoal">
                        {data.parent_tip}
                      </p>
                    </div>
                  </motion.div>
                )}
                {loading && (
                  <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="italic-serif text-charcoal/60 text-lg">
                    Charlie is thinking…
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          <div className="md:col-span-5 flex items-center">
            <div className="relative w-full">
              <div className="absolute -inset-6 spotlight rounded-[42%_58%_46%_54%/56%_44%_58%_42%]" aria-hidden />
              <img
                src="https://customer-assets-jt897jd0.emergentagent.net/job_1a124864-995c-400b-ba09-67c4486545fa/artifacts/jvns3wo9_Charlie%20teal%20background%20sketch.jpeg"
                alt="Charlie the owl"
                className="relative w-full max-w-md mx-auto rounded-[38%_62%_44%_56%/54%_46%_54%_46%]"
              />
              <div className="italic-serif text-xs text-charcoal/55 mt-4 text-center">
                Illustrations by Zuzana Svobodová
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
